import config from '../config';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private baseUrl: string;
  private getToken: () => string | null;

  constructor(baseUrl: string, getToken: () => string | null) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.getToken = getToken;
  }

  private buildUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${cleanPath}`;
  }

  private getHeaders(includeAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  async get<T>(path: string, requiresAuth: boolean = true): Promise<T> {
    const url = this.buildUrl(path);
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(requiresAuth)
    });

    return this.handleResponse<T>(response);
  }

  async post<TRequest, TResponse>(
    path: string,
    body: TRequest,
    requiresAuth: boolean = true
  ): Promise<TResponse> {
    const url = this.buildUrl(path);
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(requiresAuth),
      body: JSON.stringify(body)
    });

    return this.handleResponse<TResponse>(response);
  }

  async put<TRequest, TResponse>(
    path: string,
    body: TRequest,
    requiresAuth: boolean = true
  ): Promise<TResponse> {
    const url = this.buildUrl(path);
    const response = await fetch(url, {
      method: 'PUT',
      headers: this.getHeaders(requiresAuth),
      body: JSON.stringify(body)
    });

    return this.handleResponse<TResponse>(response);
  }

  async delete<T>(path: string, requiresAuth: boolean = true): Promise<T> {
    const url = this.buildUrl(path);
    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(requiresAuth)
    });

    return this.handleResponse<T>(response);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    let parsed: unknown;

    try {
      parsed = text ? JSON.parse(text) : {};
    } catch {
      parsed = text;
    }

    if (!response.ok) {
      throw new ApiError(
        `Request failed with status ${response.status}`,
        response.status,
        parsed
      );
    }

    return parsed as T;
  }
}

// Create a singleton instance with token management
let currentToken: string | null = null;

export function setAuthToken(token: string | null) {
  currentToken = token;
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
}

export function getAuthToken(): string | null {
  if (!currentToken) {
    currentToken = localStorage.getItem('authToken');
  }
  return currentToken;
}

export function getAuthUserId(): string | null {
  const token = getAuthToken();
  if (!token) {
    return null;
  }
  const parts = token.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    
    // Debug: log all claims to help identify the correct one
    console.log('[getAuthUserId] JWT payload claims:', Object.keys(payload));
    console.log('[getAuthUserId] Full payload:', payload);
    
    // Check various claim names for user ID
    // Prioritize standard JWT 'sub' claim, then .NET alternatives
    const possibleUserIdClaims = [
      'sub',  // Standard JWT claim for subject/user ID
      'nameid',  // .NET short form
      'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier',  // .NET full URI
      'nameidentifier',
      'uid',
      'user_id',
      'userId',
      'id',
    ];
    
    for (const claimName of possibleUserIdClaims) {
      if (payload?.[claimName]) {
        console.log(`[getAuthUserId] Found userId in claim '${claimName}':`, payload[claimName]);
        return payload[claimName];
      }
    }
    
    console.warn('[getAuthUserId] Could not find userId in any known claim');
    return null;
  } catch (e) {
    console.error('[getAuthUserId] Error parsing token:', e);
    return null;
  }
}

export const apiClient = new ApiClient(config.apiBaseUrl, getAuthToken);

// Identity API client (for authentication endpoints - now served from main API, no auth token needed)
export const identityApiClient = new ApiClient(config.apiBaseUrl, () => null);
